/**
 * Founder home page. Shows the four feature cards (Data Room, Fundraising Plan,
 * Investor Hygiene, CEG Funding) and a directory of the founder's available
 * EIRs, flagged primary/secondary.
 *
 * The data-room card displays a live progress ring once getDataRoom resolves;
 * getMyEIRs failures are swallowed so the page still renders without the EIR
 * directory.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDataRoom, getMyEIRs } from '../services/apiService';
import CircularProgress from '../components/CircularProgress';
import { useAuth } from '../context/AuthContext';
import FounderNotesWidget from '../components/FounderNotesWidget';

function FundraisingIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="30" stroke="var(--color-forest)" strokeWidth="2.5" />
      <path d="M32 16v32M42 23H27a6 6 0 000 12h10a6 6 0 010 12H20" stroke="var(--color-forest)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CRMIcon() {
  return (
    <svg width="120" height="120" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="30" stroke="var(--color-forest)" strokeWidth="2.5" />
      <circle cx="32" cy="24" r="7" stroke="var(--color-forest)" strokeWidth="2.5" />
      <path d="M14 50c0-9.94 8.06-18 18-18s18 8.06 18 18" stroke="var(--color-forest)" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// function FundingIcon() {
//   return (
//     <svg width="120" height="120" viewBox="0 0 64 64" fill="none">
//       <circle cx="32" cy="32" r="30" stroke="var(--color-forest)" strokeWidth="2.5" />
//       <rect x="16" y="28" width="32" height="20" rx="2.5" stroke="var(--color-forest)" strokeWidth="2.5" />
//       <path d="M40 28v-3a8 8 0 00-16 0v3" stroke="var(--color-forest)" strokeWidth="2.5" strokeLinejoin="round" />
//       <line x1="32" y1="34" x2="32" y2="42" stroke="var(--color-forest)" strokeWidth="2.5" strokeLinecap="round" />
//       <line x1="28" y1="38" x2="36" y2="38" stroke="var(--color-forest)" strokeWidth="2.5" strokeLinecap="round" />
//     </svg>
//   );
// }


/** Founder landing page: feature cards plus the assigned/available EIR directory. */
export default function FounderDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dataRoomProgress, setDataRoomProgress] = useState(null);
  const [myEIRs, setMyEIRs] = useState([]);

  useEffect(() => {
    getDataRoom().then((data) => {
      setDataRoomProgress({
        percent: data.overallProgress,
        nextDueDate: data.nextDueDate,
        completedItems: data.completedItems,
        totalItems: data.totalItems,
      });
    });
    getMyEIRs().then(setMyEIRs).catch(() => {});
  }, []);

  return (
    <div style={styles.page}>
      <style>{`
        .founder-cards {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          padding: 0 5%;
          margin-bottom: var(--space-10, 52px);
        }
        .founder-cards > div {
          min-width: 260px;
        }
        .founder-cards svg {
          width: clamp(60px, 9vw, 120px);
          height: clamp(60px, 9vw, 120px);
        }
        @media (max-width: 700px) {
          .founder-cards {
            flex-direction: column;
            padding: 0;
          }
          .founder-cards > div {
            flex: none;
            min-width: 0;
            padding: 24px 28px;
          }
          .founder-cards svg {
            width: 90px;
            height: 90px;
          }
        }
      `}</style>
      <div style={styles.inner}>
        <div style={styles.header}>
          <h1 style={styles.title}>Venture Readiness</h1>
          <p style={styles.subtitle}>
            {user
              ? `Welcome back, ${user.first_name}. Here's where you stand.`
              : 'Track your fundraising readiness across key milestones.'}
          </p>
        </div>

        <div className="founder-cards">
          <FeatureCard
            title="Data Room"
            description="Upload and organize your key documents for investors, and track your progress with our checklist."
            onClick={() => navigate('/data-room')}
          >
            {dataRoomProgress ? (
              <CircularProgress
                percent={dataRoomProgress.percent}
                size={120}
                stroke={10}
              />
            ) : (
              <div style={styles.iconPlaceholder} />
            )}
          </FeatureCard>
          <FeatureCard
            title="Fundraising Plan"
            description="Once you fill out the questionnaire, we'll generate a customized fundraising plan with next steps and resources tailored to your stage and goals."
            onClick={() => navigate('/fundraising')}
          >
            <FundraisingIcon />
          </FeatureCard>
          <FeatureCard
            title="Investor Hygiene"
            description="Keep track of your investor pipeline in one place — manage contacts, notes, and next steps designed for founders."
            onClick={() => navigate('/crm')}
          >
            <CRMIcon />
          </FeatureCard>
          {/* <FeatureCard
            title="CEG Funding"
            description="Apply for CEG micro-grant funding to cover eligible startup expenses like legal fees, filing costs, and more."
            onClick={() => navigate('/funding')}
          >
            <FundingIcon />
          </FeatureCard> */}
        </div>

        <div style={styles.eirSection}>
          <h2 style={styles.eirSectionTitle}>Entrepreneurs-in-Residence</h2>
          <p style={styles.eirSectionSubtitle}>Meet the EIRs available to support you. Book time with your assigned EIR or reach out to any of them.</p>
          {myEIRs.length === 0 ? (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, fontStyle: 'italic' }}>No EIRs available yet.</p>
          ) : (
            <div style={styles.eirGrid}>
              {myEIRs.map((eir) => (
                <EIRCard key={eir.id} eir={eir} />
              ))}
            </div>
          )}
        </div>

      </div>
      <FounderNotesWidget />
    </div>
  );
}

// Single EIR profile card: avatar/initials, expertise tags (capped at 4), a
// truncated bio, and booking/LinkedIn links. is_primary marks the founder's
// assigned EIR; is_secondary marks an additional assignment.
function EIRCard({ eir }) {
  const initials = `${eir.first_name?.[0] || ''}${eir.last_name?.[0] || ''}`.toUpperCase();
  const expertiseTags = eir.expertise ? eir.expertise.split(',').map((t) => t.trim()).filter(Boolean) : [];

  return (
    <div style={styles.eirCard}>
      <div style={styles.eirCardTop}>
        <div style={styles.eirAvatar}>
          {eir.avatar_url
            ? <img src={eir.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            : <span style={styles.eirInitials}>{initials}</span>
          }
        </div>
        <div style={styles.eirInfo}>
          <div style={styles.eirNameRow}>
            <p style={styles.eirName}>{eir.first_name} {eir.last_name}</p>
            {eir.is_primary && (
              <span style={{ ...styles.eirBadge, ...styles.primaryBadge }}>Your EIR</span>
            )}
            {eir.is_secondary && !eir.is_primary && (
              <span style={{ ...styles.eirBadge, ...styles.secondaryBadge }}>Assigned</span>
            )}
          </div>
          {expertiseTags.length > 0 && (
            <div style={styles.eirTags}>
              {expertiseTags.slice(0, 4).map((tag) => (
                <span key={tag} style={styles.eirTag}>{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {eir.bio && (
        <p style={styles.eirBio}>{eir.bio.length > 160 ? `${eir.bio.slice(0, 160)}…` : eir.bio}</p>
      )}

      <div style={styles.eirCardDivider} />

      <div style={styles.eirCardActions}>
        {eir.booking_link ? (
          <a
            href={eir.booking_link}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.bookBtn}
          >
            Book a Meeting
          </a>
        ) : (
          <span style={styles.noBooking}>No meeting link set</span>
        )}
        {eir.linkedin && (
          <a
            href={eir.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkedinBtn}
          >
            LinkedIn
          </a>
        )}
      </div>
    </div>
  );
}

function FeatureCard({ title, description, children, onClick }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardIcon}>{children}</div>
      <p style={styles.cardTitle}>{title}</p>
      {description && <p style={styles.cardDescription}>{description}</p>}
      <button style={styles.viewBtn} onClick={onClick}>View</button>
    </div>
  );
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { margin: '0 auto' },
  header: { textAlign: 'center', marginBottom: 'var(--space-10)', paddingTop: 'var(--space-6)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 'var(--font-size-page-title)', fontWeight: 800, color: 'var(--color-rust)', lineHeight: 1.05, marginBottom: 'var(--space-3)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 'var(--font-size-page-subtitle)', fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.6, maxWidth: 520, margin: '0 auto' },
  card: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', padding: '28px 40px', flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-3)' },
  cardIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120 },
  iconPlaceholder: { width: 120, height: 120, borderRadius: 'var(--radius-full)', background: 'var(--color-mint-dark)' },
  cardTitle: { fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-card-title)', fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', lineHeight: 1.3 },
  cardDescription: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6, textAlign: 'center', lineHeight: 1.6, maxWidth: 600 },
  viewBtn: { padding: '12px 44px', borderRadius: 'var(--radius-full)', fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid var(--color-forest)', cursor: 'pointer' },

  eirSection: { padding: '0 10%', marginBottom: 'var(--space-10)' },
  eirSectionTitle: { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: 'var(--color-forest)', marginBottom: 4 },
  eirSectionSubtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.55, marginBottom: 24 },
  eirGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 },

  eirCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', padding: '24px', display: 'flex', flexDirection: 'column', gap: 12 },
  eirCardTop: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  eirAvatar: { width: 52, height: 52, borderRadius: '50%', background: 'var(--color-mint-dark)', border: '2px solid var(--color-mint-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  eirInitials: { fontFamily: 'var(--font-accent)', fontSize: 18, fontWeight: 800, color: 'var(--color-forest)' },
  eirInfo: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  eirNameRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  eirName: { fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--color-forest)', lineHeight: 1.2 },
  eirBadge: { fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-full)', padding: '2px 9px', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 },
  primaryBadge: { background: '#dcfce7', color: '#15803d' },
  secondaryBadge: { background: '#f0e8ff', color: '#5b21b6' },
  eirTags: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  eirTag: { fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, background: 'var(--color-mint-dark)', color: 'var(--color-forest)', borderRadius: 'var(--radius-full)', padding: '2px 8px' },
  eirBio: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.65, lineHeight: 1.6, margin: 0 },
  eirCardDivider: { height: 1, background: 'var(--color-mint-dark)' },
  eirCardActions: { display: 'flex', alignItems: 'center', gap: 10 },
  bookBtn: { display: 'inline-block', padding: '8px 20px', borderRadius: 'var(--radius-full)', fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-body)', color: '#fff', background: 'var(--color-forest)', textDecoration: 'none', border: 'none', cursor: 'pointer' },
  noBooking: { fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-forest)', opacity: 0.4, fontStyle: 'italic' },
  linkedinBtn: { display: 'inline-block', padding: '8px 16px', borderRadius: 'var(--radius-full)', fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--color-forest)', background: 'none', border: '1.5px solid var(--color-mint-dark)', textDecoration: 'none' },
};
